import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../api/client'

export interface FeatureFlags {
  max_prints_per_month: number
  jobboss_integration: boolean
  approval_workflow: boolean
  training_system: boolean
  assembly_support: boolean
  api_access: boolean
}

interface FeatureContextType {
  tier: string
  features: FeatureFlags
  loading: boolean
  hasFeature: (name: keyof FeatureFlags) => boolean
}

const DEFAULT_FEATURES: FeatureFlags = {
  max_prints_per_month: 500,
  jobboss_integration: true,
  approval_workflow: true,
  training_system: true,
  assembly_support: true,
  api_access: false,
}

const FeatureContext = createContext<FeatureContextType>({
  tier: 'pro',
  features: DEFAULT_FEATURES,
  loading: true,
  hasFeature: () => true,
})

export function FeatureProvider({ children }: { children: ReactNode }) {
  const [tier, setTier] = useState('pro')
  const [features, setFeatures] = useState<FeatureFlags>(DEFAULT_FEATURES)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get('/features')
      .then((res) => {
        setTier(res.data.tier)
        setFeatures(res.data.features)
      })
      .catch(() => {
        // Fall back to defaults (pro tier)
      })
      .finally(() => setLoading(false))
  }, [])

  const hasFeature = (name: keyof FeatureFlags): boolean => {
    const value = features[name]
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    return false
  }

  return (
    <FeatureContext.Provider value={{ tier, features, loading, hasFeature }}>
      {children}
    </FeatureContext.Provider>
  )
}

export function useFeatures() {
  return useContext(FeatureContext)
}
